import { isEmptyValue, isValidDate } from "./validations"

export const NONE_PLACEHOLDER = '—'

/** Display names for the Donate API's payment and subscription processors. */
export function formatProcessorLabel(processor: string | null | undefined): string {
  const value = processor?.trim()
  if (!value) return NONE_PLACEHOLDER
  const labels: Record<string, string> = {
    authorize: 'Authorize.net',
    paypal: 'PayPal',
    paypal_rest: 'PayPal V2',
  }
  return labels[value] ?? value
}

export type DateInput = Date | string | null | undefined

export function parseDateInput(value: DateInput): Date | null {
  if (!value) return null
  if (value instanceof Date) return isValidDate(value) ? value : null
  if (typeof value !== 'string') return null
  const trimmed = value.trim()
  if (!trimmed) return null
  const d = new Date(trimmed)
  return isValidDate(d) ? d : null
}

const DATE_ONLY_REGEX = /^\d{4}-\d{2}-\d{2}$/

// No time component
export function formatDateLocal(value: DateInput): string {
  const date = parseDateInput(value)
  if (!date) return '—'
  return new Intl.DateTimeFormat('en-US', { month: 'short', day: '2-digit', year: 'numeric' }).format(date)
}

export function formatDateTimeLocal (
  value: DateInput,
  options?: {
    placeholder?: string
    invalid?: string
    fallbackToInput?: boolean
    locale?: string
    intlOptions?: Intl.DateTimeFormatOptions
  },
): string {
  const placeholder = options?.placeholder ?? '—'
  const invalid = options?.invalid ?? placeholder

  if (!value) return placeholder

  const raw = typeof value === 'string' ? value : null

  // Date-only strings (YYYY-MM-DD) should be formatted without timezone conversion
  if (raw && DATE_ONLY_REGEX.test(raw)) {
    const [year, month, day] = raw.split('-').map(Number)
    const localDate = new Date(year, month - 1, day)
    if (!isValidDate(localDate)) return invalid

    if (options?.intlOptions) {
      return new Intl.DateTimeFormat(options?.locale, options.intlOptions).format(localDate)
    }
    return new Intl.DateTimeFormat(options?.locale ?? 'en-US', {
      month: 'numeric',
      day: 'numeric',
      year: 'numeric',
    }).format(localDate)
  }

  const date = parseDateInput(value)
  if (!date) {
    if (options?.fallbackToInput && raw) return raw
    return invalid
  }

  if (options?.intlOptions) {
    return new Intl.DateTimeFormat(options?.locale, options.intlOptions).format(date)
  }

  return options?.locale ? date.toLocaleString(options.locale) : date.toLocaleString()
}

export function formatDateWithDayOfWeek(value: DateInput): string {
  const date = parseDateInput(value)
  if (!date) return '—'

  const weekday = new Intl.DateTimeFormat('en-US', { weekday: 'short' }).format(date)
  const weekdayLabel = weekday === 'Tue' ? 'Tues' : weekday === 'Thu' ? 'Thurs' : weekday

  const monthDay = new Intl.DateTimeFormat('en-US', { month: '2-digit', day: '2-digit' }).format(date)
  const time = new Intl.DateTimeFormat('en-US', { hour: 'numeric', minute: '2-digit' }).format(date)

  return `${weekdayLabel}, ${monthDay} ${time}`
}

export function titleCase(status: string): string {
  const normalized = status.trim()
  if (!normalized) return '---'
  return normalized.charAt(0).toUpperCase() + normalized.slice(1)
}

export function ordinalSuffix(day: number): string {
  if (day >= 11 && day <= 13) return 'th'
  const mod = day % 10
  if (mod === 1) return 'st'
  if (mod === 2) return 'nd'
  if (mod === 3) return 'rd'
  return 'th'
}

export function formatMonthDayOrdinal(value: DateInput, options?: { locale?: string }): string | null {
  const date = parseDateInput(value)
  if (!date) return null

  const day = date.getDate()
  const month = date.toLocaleString(options?.locale ?? 'en-US', { month: 'long' })
  return `${month} ${day}${ordinalSuffix(day)}`
}

export function formatTestimonialDate(value: string): string {
  const date = parseDateInput(value)
  if (!date) return '—'

  const weekday = new Intl.DateTimeFormat('en-US', { weekday: 'short' }).format(date)
  const weekdayLabel = weekday === 'Tue' ? 'Tues' : weekday === 'Thu' ? 'Thurs' : weekday

  const monthDay = new Intl.DateTimeFormat('en-US', { month: '2-digit', day: '2-digit' }).format(date)
  const time = new Intl.DateTimeFormat('en-US', { hour: 'numeric', minute: '2-digit' }).format(date)
  return `${weekdayLabel}, ${monthDay} ${time}`
}

export function formatCurrentWithSymbol(amount: number | string | null | undefined): string {
  return formatCurrency(amount, true)
}

export function formatCurrency(amount: number | string | null | undefined, currencySymbol = false): string {
  if (isEmptyValue(amount)) return NONE_PLACEHOLDER
  
  try {
    const amountNumber = typeof amount === 'string' ? parseFloat(amount) : amount!
    let negativeSign = amountNumber < 0 ? "-" : "";

    if (currencySymbol) {
      negativeSign = negativeSign + "$";
    }

    const absAmount = Math.abs(amountNumber || 0).toFixed(2);

    let [integerPart, decimalPart] = absAmount.split(".");

    integerPart = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ",");

    // Remove unnecessary decimal part if it's zero
    if (decimalPart === "00") {
      decimalPart = "";
    } else {
      decimalPart = "." + decimalPart;
    }

    return `${negativeSign}${integerPart}${decimalPart}`;
  } catch {
    return String(amount);
  }
}

export function parseCurrencyToCents(value: string): number | null {
  const trimmed = value.trim()
  if (!trimmed) return null

  // Common backend formats observed:
  // - "$19,631,293.37" (USD formatted string)
  // - "1963129337" (cents as an integer string)
  const normalized = trimmed.replace(/[$,\s]/g, '')
  if (!normalized) return null

  // If it includes a decimal point, treat as dollars.
  if (normalized.includes('.')) {
    const sign = normalized.startsWith('-') ? -1 : 1
    const unsigned = normalized.startsWith('-') ? normalized.slice(1) : normalized

    // Accept "12", "12.", ".12", "12.3", "12.34", "12.345" etc (as dollars).
    // We avoid floats to ensure deterministic cents conversion.
    const parts = unsigned.split('.')
    if (parts.length !== 2) return null

    const [wholeRaw, fracRaw] = parts
    const wholePart = wholeRaw.length > 0 ? wholeRaw : '0'
    const fracPart = fracRaw // may be empty (e.g., "12.")

    if (!/^\d+$/.test(wholePart)) return null
    if (fracPart.length > 0 && !/^\d+$/.test(fracPart)) return null

    // Round to nearest cent using the 3rd decimal digit (pad with zeros).
    const fracPadded = (fracPart + '000').slice(0, 3)
    const cents2 = fracPadded.slice(0, 2)
    const third = fracPadded.slice(2, 3)

    try {
      let whole = BigInt(wholePart)
      let cents = BigInt(cents2)

      if (third >= '5') {
        cents += 1n
        if (cents >= 100n) {
          cents = 0n
          whole += 1n
        }
      }

      const total = whole * 100n + cents
      const signed = sign === -1 ? -total : total

      // Preserve number return type; reject values that can't be represented safely.
      const max = BigInt(Number.MAX_SAFE_INTEGER)
      if (signed > max || signed < -max) return null

      return Number(signed)
    } catch {
      return null
    }
  }

  // Otherwise treat as cents.
  try {
    const cents = BigInt(normalized)
    const max = BigInt(Number.MAX_SAFE_INTEGER)
    if (cents > max || cents < -max) return null
    return Number(cents)
  } catch {
    return null
  }
}

export function sanitizePhoneInput(value: string): string {
  // Allow digits and common phone punctuation. Strip everything else (including letters).
  return value.replace(/[^\d()+\-\s.]/g, '')
}

export function normalizePhoneDigits(value: string | null): string {
  if (!value) return ''
  return value.replace(/\D/g, '')
}

export function formatUsPhone(value: string | null): string {
  if (!value) return NONE_PLACEHOLDER
  const digits = normalizePhoneDigits(value)
  if (!digits) return ''

  // If it's a US 11-digit number with leading "1", format as +1 (AAA) BBB-CCCC.
  if (digits.length === 11 && digits.startsWith('1')) {
    const area = digits.slice(1, 4)
    const exchange = digits.slice(4, 7)
    const line = digits.slice(7, 11)
    return `+1 (${area}) ${exchange}-${line}`
  }

  // If it's a US 10-digit number, format as (AAA) BBB-CCCC.
  if (digits.length === 10) {
    const area = digits.slice(0, 3)
    const exchange = digits.slice(3, 6)
    const line = digits.slice(6, 10)
    return `(${area}) ${exchange}-${line}`
  }

  // Otherwise, return sanitized input (keeps user-visible structure, no letters).
  return sanitizePhoneInput(value).trim()
}

export function formatUsPhoneAsYouType(value: string): string {
  const digits = normalizePhoneDigits(value)
  if (!digits) return ''

  // Country code path: 11 digits starting with 1
  if (digits.startsWith('1')) {
    const rest = digits.slice(1)
    if (rest.length === 0) return '+1'

    const area = rest.slice(0, 3)
    const exchange = rest.slice(3, 6)
    const line = rest.slice(6, 10)

    if (rest.length <= 3) return `+1 (${area}`
    if (rest.length <= 6) return `+1 (${area}) ${exchange}`
    return `+1 (${area}) ${exchange}-${line}`
  }

  // 10-digit US path
  const area = digits.slice(0, 3)
  const exchange = digits.slice(3, 6)
  const line = digits.slice(6, 10)

  if (digits.length <= 3) return `(${area}`
  if (digits.length <= 6) return `(${area}) ${exchange}`
  return `(${area}) ${exchange}-${line}`
}

export function isValidPhoneNumber(value: string): boolean {
  const digits = normalizePhoneDigits(value)
  if (!digits) return true // optional field
  if (digits.length === 10) return true
  if (digits.length === 11 && digits.startsWith('1')) return true
  return false
}

export function pad2(value: number): string {
  return String(value).padStart(2, '0')
}

export function getTimeComponents(
  totalMinutes: number | null | undefined
): { hours: string; minutes: string } | null {
  if (totalMinutes === null || totalMinutes === undefined) return null
  const minutes = Math.max(0, Math.floor(totalMinutes))
  const hours = Math.floor(minutes / 60)
  const remainingMinutes = minutes % 60
  return { hours: pad2(hours), minutes: pad2(remainingMinutes) }
}

export function formatInt(value: number | null | undefined): string {
  if (isEmptyValue(value)) return NONE_PLACEHOLDER
  if (typeof value !== 'number' || !Number.isFinite(value)) return NONE_PLACEHOLDER
  return new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(value)
}

// "Friday, July 10th · 2:00 – 3:00 PM" — date/weekday from start_at rendered in the
// report timezone; the clean clock-hour range from the nominal `hour` (so the offset
// window, e.g. :05, never leaks into the label).
export function formatCurrentHourLabel(hour: {
  start_at: string
  hour: number
  timezone: string
}): string {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: hour.timezone,
    weekday: 'long',
    month: 'long',
    day: 'numeric',
  }).formatToParts(new Date(hour.start_at))
  const weekday = parts.find((p) => p.type === 'weekday')?.value ?? ''
  const month = parts.find((p) => p.type === 'month')?.value ?? ''
  const day = Number(parts.find((p) => p.type === 'day')?.value ?? '0')
  const datePart = `${weekday}, ${month} ${day}${ordinalSuffix(day)}`
  return `${datePart} · ${formatHourRange(hour.hour)}`
}

function to12(hour24: number): { h: number; period: 'AM' | 'PM' } {
  const period = hour24 < 12 ? 'AM' : 'PM'
  const h = hour24 % 12 === 0 ? 12 : hour24 % 12
  return { h, period }
}

function formatHourRange(startHour: number): string {
  const start = to12(startHour)
  const end = to12((startHour + 1) % 24)
  return start.period === end.period
    ? `${start.h}:00 – ${end.h}:00 ${end.period}`
    : `${start.h}:00 ${start.period} – ${end.h}:00 ${end.period}`
}

/**
 * The hour window as the Tally design draws it — "1:05 – 2:04 PM" — read from the
 * report's own instants, so the bucket offset shows (unlike the nominal range in
 * `formatCurrentHourLabel`). `end_at` is exclusive, so this backs off a minute to
 * name the last minute inside the window. Meridiem is shown once when both ends
 * share it, matching `formatHourRange`.
 */
export function formatHourWindowRange(hour: { start_at: string; end_at: string; timezone: string }): string {
  const clock = (date: Date) => {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: hour.timezone,
      hour: 'numeric',
      minute: '2-digit',
    }).formatToParts(date)
    const value = (type: string) => parts.find((p) => p.type === type)?.value ?? ''
    return { time: `${value('hour')}:${value('minute')}`, period: value('dayPeriod') }
  }
  const start = clock(new Date(hour.start_at))
  const end = clock(new Date(new Date(hour.end_at).getTime() - 60_000))
  return start.period === end.period
    ? `${start.time} – ${end.time} ${end.period}`
    : `${start.time} ${start.period} – ${end.time} ${end.period}`
}

/** The date the report hour falls on, in the report's timezone — "August 12th". */
export function formatHourDateLabel(hour: { start_at: string; timezone: string }): string {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: hour.timezone,
    month: 'long',
    day: 'numeric',
  }).formatToParts(new Date(hour.start_at))
  const month = parts.find((p) => p.type === 'month')?.value ?? ''
  const day = Number(parts.find((p) => p.type === 'day')?.value ?? '0')
  return `${month} ${day}${ordinalSuffix(day)}`
}
